import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  InstructionStepper,
  Layout,
  PageWrap,
  Paragraph,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Analytics } from '../../services/analytics';
import { creditCheckPageEvent } from '../../services/analytics/analyticsEvents';
import {
  creditApplicationSelectors,
  setCanRedirectToOnfido,
} from '../../services/redux/features/creditApplication.slice';
import {
  selectCreditApplicationIdFromDataStorage,
  selectQuoteIdFromDataStorage,
} from '../../services/redux/features/dataStorage.redux';
import { useNavigate } from '../../services/routing/navigate';
import { isIdCardStipulationStatusDefined, shouldCheckIdStipulationStatus } from '../FormPage/utils/checkIDStatusUtil';

export const CreditCheckSuccessPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const creditApplicationId = useSelector(selectCreditApplicationIdFromDataStorage);
  const creditApplicationDetails = useSelector(creditApplicationSelectors.selectCreditApplicationDetails);
  const quoteId = useSelector(selectQuoteIdFromDataStorage);
  const canRedirectToOnfido = useSelector(creditApplicationSelectors.selectCanRedirectToOnfido);

  const navigate = useNavigate();

  useEffect(() => {
    Analytics.push(creditCheckPageEvent('success'));
  }, []);

  useEffect(() => {
    if (
      creditApplicationDetails.creditDecision?.solvencyCheck?.status &&
      shouldCheckIdStipulationStatus(creditApplicationDetails.creditDecision?.solvencyCheck?.status)
    ) {
      const executeIdCheck = async () => {
        if (!creditApplicationId || !quoteId) {
          return;
        }

        dispatch(setCanRedirectToOnfido(await isIdCardStipulationStatusDefined(creditApplicationId, quoteId)));
      };

      executeIdCheck();
    }
  }, [creditApplicationDetails.creditDecision?.solvencyCheck?.status, creditApplicationId, dispatch, quoteId]);

  const handleRedirection = useCallback(() => {
    if (canRedirectToOnfido) {
      navigate.identLanding();
    } else {
      navigate.form('/5');
    }
  }, [navigate, canRedirectToOnfido]);

  return (
    <>
      <PageWrap>
        {/* <ContentSection pageWrap className="u-mr u-ml u-border u-mt-xlarge">
          <ShoppingCart formData={formData} dataTheme="default" />
        </ContentSection> */}
        <ContentSection className="u-border">
          <SectionHeading level={2} center style={{ marginBottom: '10px' }}>
            {t('creditCheck:heading')}
          </SectionHeading>
          <br />
          <br />
          <Layout center>
            <div className="u-block-center u-h1">
              <i className="c-icon  c-icon--[semantic-quality] c-icon--xxlarge" />
            </div>
          </Layout>
          <Layout center>
            <Layout.Item default="3/4">
              <Paragraph>{t('creditCheck:text1')}</Paragraph>
              <br />
              <Paragraph>{t('creditCheck:text2')}</Paragraph>
            </Layout.Item>
          </Layout>
          <br />
          <ContentSection pageWrap pageWrapSize="medium">
            <InstructionStepper
              statusActiveText={t('app:instructionStepper:activeLabel')}
              statusNotActiveText={t('app:instructionStepper:nonActiveLabel')}
            >
              <InstructionStepper.Step
                icon="document"
                title={t('identLandingPage:instructions:upload')}
                state="is-active"
              />
              <InstructionStepper.Step icon="checkmark" title={t('identLandingPage:instructions:analysis')} />
              <InstructionStepper.Step icon="pen" title={t('identLandingPage:instructions:sign')} />
              <InstructionStepper.Step icon="semantic-calendar" title={t('identLandingPage:instructions:vehicle')} />
            </InstructionStepper>
          </ContentSection>
        </ContentSection>

        <ContentSection>
          <Layout center>
            <Layout.Item default="1/6">
              <ButtonContainer center>
                <Button onClick={handleRedirection}>{t('creditCheck:continueButton')}</Button>
              </ButtonContainer>
            </Layout.Item>
          </Layout>
        </ContentSection>
      </PageWrap>
    </>
  );
};
